import { supabase } from './supabase'

// Database migrations
const MIGRATION_001_CREATE_TABLES = `
-- Create profiles table
CREATE TABLE IF NOT EXISTS public.profiles (
  id uuid references auth.users on delete cascade not null primary key,
  username text unique,
  display_name text,
  bio text,
  avatar_url text,
  website text,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  updated_at timestamp with time zone default timezone('utc'::text, now()) not null,
  tier text default 'free'::text,
  generation_count integer default 0,
  last_generation_at timestamp with time zone
);

-- Create images table
CREATE TABLE IF NOT EXISTS public.images (
  id uuid default gen_random_uuid() primary key,
  user_id uuid references public.profiles(id) on delete cascade not null,
  title text not null,
  description text,
  prompt_text text not null,
  image_url text not null,
  filters_used jsonb default '{}'::jsonb,
  voice_persona jsonb default '{}'::jsonb,
  generation_settings jsonb default '{}'::jsonb,
  is_public boolean default false,
  is_nsfw boolean default false,
  likes_count integer default 0,
  views_count integer default 0,
  download_count integer default 0,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  updated_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- Create favorites table
CREATE TABLE IF NOT EXISTS public.favorites (
  id uuid default gen_random_uuid() primary key,
  user_id uuid references public.profiles(id) on delete cascade not null,
  image_id uuid references public.images(id) on delete cascade not null,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  unique(user_id, image_id)
);

-- Create collections table
CREATE TABLE IF NOT EXISTS public.collections (
  id uuid default gen_random_uuid() primary key,
  user_id uuid references public.profiles(id) on delete cascade not null,
  name text not null,
  description text,
  is_public boolean default false,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  updated_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- Create collection_images junction table
CREATE TABLE IF NOT EXISTS public.collection_images (
  id uuid default gen_random_uuid() primary key,
  collection_id uuid references public.collections(id) on delete cascade not null,
  image_id uuid references public.images(id) on delete cascade not null,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  unique(collection_id, image_id)
);

-- Enable Row Level Security
ALTER TABLE public.profiles ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.images ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.favorites ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.collections ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.collection_images ENABLE ROW LEVEL SECURITY;

-- Create policies for profiles
CREATE POLICY IF NOT EXISTS "Public profiles are viewable by everyone" ON public.profiles
  FOR SELECT USING (true);

CREATE POLICY IF NOT EXISTS "Users can insert their own profile" ON public.profiles
  FOR INSERT WITH CHECK (auth.uid() = id);

CREATE POLICY IF NOT EXISTS "Users can update own profile" ON public.profiles
  FOR UPDATE USING (auth.uid() = id);

-- Create policies for images
CREATE POLICY IF NOT EXISTS "Public images are viewable by everyone" ON public.images
  FOR SELECT USING (is_public = true OR auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can insert their own images" ON public.images
  FOR INSERT WITH CHECK (auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can update own images" ON public.images
  FOR UPDATE USING (auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can delete own images" ON public.images
  FOR DELETE USING (auth.uid() = user_id);

-- Create policies for favorites
CREATE POLICY IF NOT EXISTS "Users can view own favorites" ON public.favorites
  FOR SELECT USING (auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can insert own favorites" ON public.favorites
  FOR INSERT WITH CHECK (auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can delete own favorites" ON public.favorites
  FOR DELETE USING (auth.uid() = user_id);

-- Create policies for collections
CREATE POLICY IF NOT EXISTS "Public collections are viewable by everyone" ON public.collections
  FOR SELECT USING (is_public = true OR auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can insert their own collections" ON public.collections
  FOR INSERT WITH CHECK (auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can update own collections" ON public.collections
  FOR UPDATE USING (auth.uid() = user_id);

CREATE POLICY IF NOT EXISTS "Users can delete own collections" ON public.collections
  FOR DELETE USING (auth.uid() = user_id);

-- Create policies for collection_images
CREATE POLICY IF NOT EXISTS "Users can view images in their collections" ON public.collection_images
  FOR SELECT USING (
    EXISTS (
      SELECT 1 FROM public.collections 
      WHERE collections.id = collection_images.collection_id 
      AND (collections.user_id = auth.uid() OR collections.is_public = true)
    )
  );

CREATE POLICY IF NOT EXISTS "Users can add images to their collections" ON public.collection_images
  FOR INSERT WITH CHECK (
    EXISTS (
      SELECT 1 FROM public.collections 
      WHERE collections.id = collection_images.collection_id 
      AND collections.user_id = auth.uid()
    )
  );

CREATE POLICY IF NOT EXISTS "Users can remove images from their collections" ON public.collection_images
  FOR DELETE USING (
    EXISTS (
      SELECT 1 FROM public.collections 
      WHERE collections.id = collection_images.collection_id 
      AND collections.user_id = auth.uid()
    )
  );

-- Create indexes for better performance
CREATE INDEX IF NOT EXISTS profiles_username_idx ON public.profiles(username);
CREATE INDEX IF NOT EXISTS images_user_id_idx ON public.images(user_id);
CREATE INDEX IF NOT EXISTS images_created_at_idx ON public.images(created_at DESC);
CREATE INDEX IF NOT EXISTS images_is_public_idx ON public.images(is_public);
CREATE INDEX IF NOT EXISTS favorites_user_id_idx ON public.favorites(user_id);
CREATE INDEX IF NOT EXISTS favorites_image_id_idx ON public.favorites(image_id);
CREATE INDEX IF NOT EXISTS collections_user_id_idx ON public.collections(user_id);
CREATE INDEX IF NOT EXISTS collection_images_collection_id_idx ON public.collection_images(collection_id);
CREATE INDEX IF NOT EXISTS collection_images_image_id_idx ON public.collection_images(image_id);

-- Create function to handle new user registration
CREATE OR REPLACE FUNCTION public.handle_new_user()
RETURNS trigger AS $$
BEGIN
  INSERT INTO public.profiles (id, display_name, username)
  VALUES (
    new.id,
    COALESCE(new.raw_user_meta_data->>'display_name', new.email),
    COALESCE(new.raw_user_meta_data->>'username', split_part(new.email, '@', 1))
  );
  RETURN new;
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

-- Create trigger for new user registration
DROP TRIGGER IF EXISTS on_auth_user_created ON auth.users;
CREATE TRIGGER on_auth_user_created
  AFTER INSERT ON auth.users
  FOR EACH ROW EXECUTE PROCEDURE public.handle_new_user();

-- Create function to automatically update updated_at timestamps
CREATE OR REPLACE FUNCTION public.handle_updated_at()
RETURNS trigger AS $$
BEGIN
  NEW.updated_at = timezone('utc'::text, now());
  RETURN NEW;
END;
$$ LANGUAGE plpgsql;

-- Create triggers for updated_at
DROP TRIGGER IF EXISTS handle_updated_at ON public.profiles;
CREATE TRIGGER handle_updated_at BEFORE UPDATE ON public.profiles
  FOR EACH ROW EXECUTE PROCEDURE public.handle_updated_at();

DROP TRIGGER IF EXISTS handle_updated_at ON public.images;
CREATE TRIGGER handle_updated_at BEFORE UPDATE ON public.images
  FOR EACH ROW EXECUTE PROCEDURE public.handle_updated_at();

DROP TRIGGER IF EXISTS handle_updated_at ON public.collections;
CREATE TRIGGER handle_updated_at BEFORE UPDATE ON public.collections
  FOR EACH ROW EXECUTE PROCEDURE public.handle_updated_at();
`

const MIGRATION_002_CREATE_ADMIN_USERS = `
-- Create admin_users table
CREATE TABLE IF NOT EXISTS public.admin_users (
  id uuid default gen_random_uuid() primary key,
  email text unique not null,
  role text not null CHECK (role IN ('super_admin', 'admin', 'moderator')),
  permissions jsonb default '{}'::jsonb,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  created_by uuid references auth.users(id),
  is_active boolean default true,
  last_login_at timestamp with time zone,
  notes text
);

-- Enable RLS on admin_users
ALTER TABLE public.admin_users ENABLE ROW LEVEL SECURITY;

-- Create policy - only super admins can manage admin users
CREATE POLICY IF NOT EXISTS "Only super admins can manage admin users" ON public.admin_users
  FOR ALL USING (
    EXISTS (
      SELECT 1 FROM public.admin_users AS current_admin
      WHERE current_admin.email = auth.jwt() ->> 'email'
      AND current_admin.role = 'super_admin'
      AND current_admin.is_active = true
    )
  );

-- Create function to check if a user is an admin
CREATE OR REPLACE FUNCTION public.is_user_admin(check_email text)
RETURNS boolean AS $$
BEGIN
  RETURN EXISTS (
    SELECT 1 FROM public.admin_users
    WHERE email = check_email
    AND is_active = true
  );
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

-- Create function to check if a user is a super admin
CREATE OR REPLACE FUNCTION public.is_user_super_admin(check_email text)
RETURNS boolean AS $$
BEGIN
  RETURN EXISTS (
    SELECT 1 FROM public.admin_users
    WHERE email = check_email
    AND role = 'super_admin'
    AND is_active = true
  );
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

-- Create function to get user admin role
CREATE OR REPLACE FUNCTION public.get_user_admin_role(check_email text)
RETURNS text AS $$
DECLARE
  user_role text;
BEGIN
  SELECT role INTO user_role
  FROM public.admin_users
  WHERE email = check_email
  AND is_active = true;
  
  RETURN COALESCE(user_role, 'user');
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

-- Create function to get user admin permissions
CREATE OR REPLACE FUNCTION public.get_user_admin_permissions(check_email text)
RETURNS jsonb AS $$
DECLARE
  user_permissions jsonb;
BEGIN
  SELECT permissions INTO user_permissions
  FROM public.admin_users
  WHERE email = check_email
  AND is_active = true;
  
  RETURN COALESCE(user_permissions, '{}'::jsonb);
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

-- Create admin audit log table
CREATE TABLE IF NOT EXISTS public.admin_audit_log (
  id uuid default gen_random_uuid() primary key,
  admin_email text not null,
  action text not null,
  target_type text,
  target_id text,
  details jsonb default '{}'::jsonb,
  ip_address inet,
  user_agent text,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- Enable RLS on admin_audit_log
ALTER TABLE public.admin_audit_log ENABLE ROW LEVEL SECURITY;

-- Create policy for admin audit log - only super admins can view
CREATE POLICY IF NOT EXISTS "Only super admins can view audit log" ON public.admin_audit_log
  FOR SELECT USING (
    EXISTS (
      SELECT 1 FROM public.admin_users
      WHERE email = auth.jwt() ->> 'email'
      AND role = 'super_admin'
      AND is_active = true
    )
  );

-- Create function to log admin actions
CREATE OR REPLACE FUNCTION public.log_admin_action(
  admin_email text,
  action text,
  target_type text DEFAULT NULL,
  target_id text DEFAULT NULL,
  details jsonb DEFAULT '{}'::jsonb
)
RETURNS void AS $$
BEGIN
  INSERT INTO public.admin_audit_log (admin_email, action, target_type, target_id, details)
  VALUES (admin_email, action, target_type, target_id, details);
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

-- Create indexes for admin tables
CREATE INDEX IF NOT EXISTS admin_users_email_idx ON public.admin_users(email);
CREATE INDEX IF NOT EXISTS admin_users_role_idx ON public.admin_users(role);
CREATE INDEX IF NOT EXISTS admin_users_is_active_idx ON public.admin_users(is_active);
CREATE INDEX IF NOT EXISTS admin_audit_log_admin_email_idx ON public.admin_audit_log(admin_email);
CREATE INDEX IF NOT EXISTS admin_audit_log_created_at_idx ON public.admin_audit_log(created_at DESC);

-- Insert initial super admin user
INSERT INTO public.admin_users (email, role, permissions, notes, is_active)
VALUES (
  'drew.venegas@gmail.com',
  'super_admin',
  '{
    "can_manage_admins": true,
    "can_manage_users": true,
    "can_view_analytics": true,
    "can_moderate_content": true,
    "can_manage_openai": true,
    "can_view_audit_logs": true,
    "can_manage_system_settings": true
  }'::jsonb,
  'Initial super admin account created during setup',
  true
)
ON CONFLICT (email) DO UPDATE SET
  role = EXCLUDED.role,
  permissions = EXCLUDED.permissions,
  is_active = EXCLUDED.is_active,
  notes = EXCLUDED.notes;
`

export interface DatabaseSetupResult {
  success: boolean
  message: string
  error?: string
  tablesCreated?: string[]
}

export const runDatabaseSetup = async (): Promise<DatabaseSetupResult> => {
  try {
    console.log('🚀 Starting automated database setup...')
    
    // Check if user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    
    if (authError || !user) {
      return {
        success: false,
        message: 'Authentication required',
        error: 'You must be logged in to run database setup'
      }
    }

    console.log('✅ User authenticated, checking database status...')

    // Try to create tables by attempting to query them
    const tablesCreated = []
    const tablesToCheck = [
      { name: 'profiles', created: false },
      { name: 'images', created: false },
      { name: 'favorites', created: false },
      { name: 'collections', created: false }
    ]

    // Check which tables already exist
    for (const table of tablesToCheck) {
      try {
        const { data, error } = await supabase.from(table.name).select('*').limit(1)
        if (!error) {
          table.created = true
          tablesCreated.push(table.name)
          console.log(`✅ Table ${table.name} already exists`)
        }
      } catch (e) {
        console.log(`❌ Table ${table.name} does not exist`)
      }
    }

    if (tablesCreated.length === tablesToCheck.length) {
      return {
        success: true,
        message: 'Database is already set up! All tables exist.',
        tablesCreated
      }
    }

    // If tables don't exist, we need manual setup
    const missingTables = tablesToCheck.filter(t => !t.created).map(t => t.name)
    
    return {
      success: false,
      message: `Database setup requires manual steps. Missing tables: ${missingTables.join(', ')}`,
      error: 'Please run the SQL migrations manually in Supabase Dashboard'
    }

  } catch (error) {
    console.error('❌ Database setup error:', error)
    return {
      success: false,
      message: 'Database setup failed with unexpected error',
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

export const checkDatabaseStatus = async (): Promise<{
  isSetup: boolean
  missingTables: string[]
  existingTables: string[]
  userProfileExists?: boolean
  authStatus?: string
}> => {
  const requiredTables = ['profiles', 'images', 'favorites', 'collections']
  const existingTables: string[] = []
  const missingTables: string[] = []
  let userProfileExists = false
  let authStatus = 'unknown'

  // Check authentication status
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError) {
      authStatus = `error: ${authError.message}`
    } else if (user) {
      authStatus = `authenticated: ${user.email}`
      
      // Check if user profile exists
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', user.id)
          .single()
        
        if (!profileError && profile) {
          userProfileExists = true
        }
      } catch (e) {
        // Profile check failed, but we'll still check tables
      }
    } else {
      authStatus = 'not authenticated'
    }
  } catch (e) {
    authStatus = `auth check failed: ${e instanceof Error ? e.message : 'unknown error'}`
  }

  // Check tables
  for (const table of requiredTables) {
    try {
      const { data, error } = await supabase.from(table).select('*').limit(1)
      if (error) {
        console.log(`❌ Table ${table}: ${error.message}`)
        missingTables.push(table)
      } else {
        console.log(`✅ Table ${table}: exists`)
        existingTables.push(table)
      }
    } catch (e) {
      console.log(`❌ Table ${table}: ${e instanceof Error ? e.message : 'unknown error'}`)
      missingTables.push(table)
    }
  }

  const isSetup = missingTables.length === 0 && (authStatus.startsWith('authenticated') ? userProfileExists : true)

  console.log('📊 Database Status Check:', {
    isSetup,
    existingTables,
    missingTables,
    userProfileExists,
    authStatus
  })

  return {
    isSetup,
    missingTables,
    existingTables,
    userProfileExists,
    authStatus
  }
} 